import {
    GeoLocationReason,
    GeoValidationResult,
    IGeoComplyError,
    IGeoComplyFailure,
    IGeoComplyResponse,
    IGeoLocationStrategy,
    IGeoRunResult
} from "../strategies/geo-location.strategy.interface";
import {IServiceFactoryExtended} from "../../service-factory-extended.interface";
import {
    GeoComplyFailureReason,
    isGeoComplyPluginInstallationRequired,
    isRetryableGeoComplyError
} from "../strategies/geo-comply-error-codes";
import {delay} from "../../../../utils/delay";
import {Check, TimeSpan} from "@sparkware/app-utils";
import {
    GeoGameRestrictedDialogButton,
    GeoKickDialogButtons,
    GeoKickDialogResult,
} from "./geo-location-dialogs-factory";
import {IModalDialogHandler} from "../../dialog/dialog.service.interface";
import {IGeoRestrictionsUpdateEventParams} from "../../sdk/sdk-events";
import {UserController_InitGeoRetryResponse} from "../../api/api.service";
import {
    GeoRunnerName,
    GeoRunParams,
    IGeoRetryOnOpenRealGame,
    ILoginGeoLocationOptions,
    IOpenGameGeoLocationOptions,
    IPeriodicGeoUpdateOptions
} from "./geo-location-runner.interface";
import {GeoLocationRunnerBase} from "./geo-location-runner-base";
import {GeoAnalyticsContext, GeoAnalyticsEvent} from "../geo-location-analytics";
import {PlatformGeoLocationValidator} from "./platform-geo-location-validator";
import {isValidGeoKickEvent} from "./geo-kick-reasons";
import {KICK_REASON} from "../../../../sdk/src/services/player_messages_service/player_messages_consts";
import {LocalStorageKeys} from "../../storage/local-storage-keys";
import {formatErrorForLogging} from "../geo-helpers";

interface IRetryGeoLocationOptions {
    reason: GeoLocationReason;
    showProgressIndicator: boolean;
}

interface IRunGeoLocationOptions {
    geoStrategyRunParams: GeoRunParams;
    showProgressIndicator: boolean;
    isPeriodicFlow: boolean;
}



export class GeoLocationRunnerInCasino extends GeoLocationRunnerBase {
    constructor(private readonly geoStrategy: IGeoLocationStrategy,
                analyticsContext: GeoAnalyticsContext,
                services: IServiceFactoryExtended) {
        super(services, analyticsContext);

    }

    protected _getRunnerName(): GeoRunnerName {
        return "Casino";
    }

    private get geoRetryCount(): number {
        const storedValue = this.services.localStorage.getItem(LocalStorageKeys.geoLocationRetryCount);
        if(Check.isEmpty(storedValue)) {
            return 0;
        }

        return parseInt(storedValue);
    }

    private set geoRetryCount(value: number) {
        this.services.localStorage.setItem(LocalStorageKeys.geoLocationRetryCount, value);
    }

    loginGeoLocation(options: ILoginGeoLocationOptions): Promise<IGeoRunResult> {
        return this._executeGeoFlow('loginGeoLocation', () => {
            this._analytics.setTransactionId(options.geoStrategyRunParams.locationTransactionId);

            this._analytics.reportEvent({
                geoEventName: GeoAnalyticsEvent.StartLoginGeo
            });

            return this._runGeoLocation({
                ...options,
                showProgressIndicator: true,
                isPeriodicFlow: false
            });
        });
    }

    openGameGeoLocation(options: IOpenGameGeoLocationOptions): Promise<IGeoRunResult> {
        return this._executeGeoFlow('openGameGeoLocation', () => {
            this._analytics.setTransactionId(options.geoStrategyRunParams.locationTransactionId);

            this._analytics.reportEvent({
                geoEventName: GeoAnalyticsEvent.StartOpenGameGeo,
                gameType: options.gameType
            });

            return this._runGeoLocation({
                ...options,
                showProgressIndicator: true,
                isPeriodicFlow: false
            });
        });
    }

    geoRetryOnOpenRealGame(options: IGeoRetryOnOpenRealGame): Promise<IGeoRunResult> {
        return this._executeGeoFlow('geoRetryOnOpenRealGame',() => {

            this._analytics.reportEvent({
                geoEventName: GeoAnalyticsEvent.StartRetryOnOpenGame,
                gameType: options.gameType
            });

            return this._retryGeoLocation({
                reason: GeoLocationReason.OpenRealGame,
                showProgressIndicator: options.showProgressIndicator
            })
        });
    }

    runPeriodicGeo(options: IPeriodicGeoUpdateOptions): Promise<IGeoRunResult> {
        return this._executeGeoFlow('runPeriodicGeo', () => {
            this._analytics.setTransactionId(options.geoStrategyRunParams.locationTransactionId);

            this._analytics.reportEvent({
                geoEventName: GeoAnalyticsEvent.PeriodicGeo
            });

            return this._runGeoLocation({
                geoStrategyRunParams: {
                    ...options.geoStrategyRunParams,
                    reason: GeoLocationReason.Periodic
                },
                showProgressIndicator: false,
                isPeriodicFlow: true
            })
        });
    }

    geoKick(params: IGeoRestrictionsUpdateEventParams): Promise<IGeoRunResult> {
        return this._executeGeoFlow('geoKick', () => {
            return this._executeGeoKick({
                kickParams: params
            });
        });

    }

    showGameRestrictedPopup(geoResult?: IGeoRunResult | null): void {
        if(!isGeoComplyPluginInstallationRequired(geoResult?.geoComplyFailure?.geoComplyError?.errorCode)) {
            this._geoDialogs.showGeoRestrictionPopup({
                buttons: GeoGameRestrictedDialogButton.LogoutContactUs
            });
        }
    }

    async showGenericGeoKickMessage(): Promise<void> {
        await this._geoDialogs.showGeoKickDialog({
            buttons: GeoKickDialogButtons.LogoutContactUs,
            troubleShooterMessages: [],
            kickReasonId: KICK_REASON.KickUserTechProblem,
            isGenericKickMessage: true
        });
    }

    private _isGeoRetryEnabled(): boolean {
        return this.services.configuration.servicesConfigs.GeoLocation.retryEnabled;
    }
    private _isRetryableGeoKick(kickParams: IGeoRestrictionsUpdateEventParams): boolean {
        return this._isGeoRetryEnabled() && Boolean(kickParams.TroubleshooterRetry);
    }

    private async _executeGeoKick(options: {kickParams: IGeoRestrictionsUpdateEventParams,
                                            geoComplyFailure?: IGeoComplyFailure,
                                            progressIndicator?: IModalDialogHandler
                                            }): Promise<IGeoRunResult> {
        const {kickParams, progressIndicator, geoComplyFailure} = options;

        this._analytics.reportEvent({
            geoEventName: GeoAnalyticsEvent.GeoKick,
            kickParams: kickParams,
            errorMessage: geoComplyFailure?.geoComplyError?.errorMessage,
            errorCode: geoComplyFailure?.geoComplyError?.errorCode,
            failureReason: geoComplyFailure?.additionalFailureReason
        });

        progressIndicator?.hide();

        const dialogResult = await this._geoDialogs.showGeoKickDialog({
            buttons: this._isRetryableGeoKick(kickParams) ? GeoKickDialogButtons.RetryContactUs : GeoKickDialogButtons.LogoutContactUs,
            kickReasonId: kickParams.KickReasonId,
            troubleShooterMessages: kickParams.DetailedMessages,
            isGenericKickMessage: false
        });

        progressIndicator?.display();

        switch (dialogResult) {
            case GeoKickDialogResult.Retry:
                this._analytics.reportEvent({
                    geoEventName: GeoAnalyticsEvent.TroubleshooterRetry
                });
                return await this._retryGeoLocation({
                    reason: GeoLocationReason.Retry,
                    showProgressIndicator: !Boolean(progressIndicator) // if we already have a progress indicator then don't show another one
                });
            default:
                return {
                    validationResult: GeoValidationResult.Fail,
                    kickParams: kickParams,
                    geoComplyFailure: geoComplyFailure
                };

        }
    }

    private async _retryGeoLocation(options: IRetryGeoLocationOptions): Promise<IGeoRunResult> {
        if(!this._isGeoRetryEnabled()) {
            return {
                validationResult: GeoValidationResult.Fail
            };
        }

        if(this.geoRetryCount >= this.services.configuration.servicesConfigs.GeoLocation.maxRetries) {
            this._analytics.reportEvent({
                geoEventName: GeoAnalyticsEvent.MaxRetryReached
            });
            return {
                validationResult: GeoValidationResult.Fail
            };
        }

        let geoParams: UserController_InitGeoRetryResponse;

        try {
            geoParams = await this.services.api.userController.initGeoRetry();
            this._analytics.setTransactionId(geoParams.LocationTransactionToken);
            this._analytics.reportEvent({
                geoEventName: GeoAnalyticsEvent.InitRetrySuccess
            });

        } catch (err) {
            this._analytics.reportEvent({
                geoEventName: GeoAnalyticsEvent.InitRetryFailure,
                errorMessage: formatErrorForLogging(err),
                failureReason: GeoComplyFailureReason.TechnicalProblem
            });

            return {
                validationResult: GeoValidationResult.Fail
            }
        }

        try {
            return await this._runGeoLocation({
                isPeriodicFlow: false,
                showProgressIndicator: options.showProgressIndicator,
                geoStrategyRunParams: {
                    reason: options.reason,
                    licenseKey: geoParams.GeoComplyLicense,
                    locationTransactionId: geoParams.LocationTransactionToken,
                    mobileNumber: this.services.user.userData.mobileNumber
                }
            })
        } finally {
            this.geoRetryCount++;
        }

    }

    private async _runGeoLocation(options: IRunGeoLocationOptions): Promise<IGeoRunResult> {

        this._analytics.setTransactionId(options.geoStrategyRunParams.locationTransactionId);

        let progressIndicator: IModalDialogHandler | undefined = undefined;

        if(options.showProgressIndicator) {
            progressIndicator = this._geoDialogs.showGeoProgressIndicator();
        }

        try {
            const geoStartTime = Date.now();


            let geoComplyResponse: IGeoComplyResponse;
            let retries = 0;
            const maxRetries = 5;

            const shouldRetry = (geComplyError: IGeoComplyError | undefined) => {
                return retries < maxRetries && geComplyError && isRetryableGeoComplyError(geComplyError);
            }

            const geoComplyDurations: number[] = [];

            do {
                const geoComplyStartTime = Date.now();

                geoComplyResponse = await this.geoStrategy.runGeoLocation({
                    ...options.geoStrategyRunParams,
                    progressIndicator: progressIndicator,
                    analytics: this._analytics
                });

                const geoComplyDuration = Date.now() - geoComplyStartTime;

                geoComplyDurations.push(geoComplyDuration);

                retries++;
                if(shouldRetry(geoComplyResponse.failure?.geoComplyError)) {
                    this._analytics.reportEvent({
                        geoEventName: GeoAnalyticsEvent.OrbitAutoRetry,
                        tryCount: retries,
                        errorCode: geoComplyResponse.failure.geoComplyError?.errorCode,
                        errorMessage: geoComplyResponse.failure.geoComplyError?.errorMessage,
                        failureReason: geoComplyResponse.failure.additionalFailureReason,
                        durations: {
                            geoComply: geoComplyDuration
                        }
                    });
                    await delay(TimeSpan.fromSeconds(1));
                }

            } while(shouldRetry(geoComplyResponse.failure?.geoComplyError));


            const platformGeoValidationStartTime = Date.now();
            const platformGeoValidationResult = await new PlatformGeoLocationValidator(this.services, this._analytics).validateGeoResult({
                geoComplyResponse: geoComplyResponse,
                isPeriodicFlow: options.isPeriodicFlow
            });

            this._analytics.reportGeoDuration({
                geoComply: TimeSpan.fromMilliseconds(geoComplyDurations.sum(t => t) / geoComplyDurations.length).totalSeconds,
                platformValidation: TimeSpan.fromMilliseconds(Date.now() - platformGeoValidationStartTime).totalSeconds,
                total: TimeSpan.fromMilliseconds(Date.now() - geoStartTime).totalSeconds,
                attempts: geoComplyDurations.length
            });


            if(platformGeoValidationResult.result !== GeoValidationResult.Pass && isValidGeoKickEvent(platformGeoValidationResult.kickParams)) {
                return await this._executeGeoKick({
                    kickParams: platformGeoValidationResult.kickParams,
                    geoComplyFailure: geoComplyResponse.failure,
                    progressIndicator: progressIndicator
                });
            } else {
                return {
                    validationResult: platformGeoValidationResult.result,
                    geoComplyFailure: geoComplyResponse.failure,
                    kickParams: platformGeoValidationResult.kickParams
                };
            }

        } finally {
            progressIndicator?.close();
        }

    }

}

